import React, { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import type { ApiError } from "@repo/api";
import { notifyGlobalToast } from "@repo/core";

// Create MutationCache with global error handling
const mutationCache = new MutationCache({
  onError: (error: Error) => {
    console.log("Global mutation error handler:", error);

    // Type guard for ApiError
    const apiError = error as unknown as ApiError;

    // Handle authentication errors
    if (apiError.status === 401) {
      if (apiError.message === "TOKEN_REFRESHED_RETRY_NEEDED") {
        // Token was refreshed successfully and retry is happening
        notifyGlobalToast("Session renewed, retrying...", "info");
      } else if (apiError.message === "Authentication failed - please login again") {
        // Token refresh failed
        notifyGlobalToast("Your session has expired. Please log in again.", "error");
      }
    }
    // Handle other errors (mutations can still add their own onError handlers)
  },
});

// Create QueryClient instance
const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
    },
  },
});

// Create context for QueryClient
const QueryClientContext = createContext<QueryClient | null>(null);

// Context provider props
interface QueryClientContextProviderProps {
  children: ReactNode;
}

// Context provider component
export const QueryClientContextProvider: React.FC<
  QueryClientContextProviderProps
> = ({ children }) => {
  return (
    <QueryClientContext.Provider value={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </QueryClientContext.Provider>
  );
};

// Custom hook to use the QueryClient
export const useQueryClient = (): QueryClient => {
  const context = useContext(QueryClientContext);

  if (!context) {
    throw new Error(
      "useQueryClient must be used within a QueryClientContextProvider"
    );
  }

  return context;
};

export default QueryClientContextProvider;
